import React, { useRef, useEffect, useState } from "react";
import { User, Bot, MicOff } from "lucide-react";

import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import {
  useFetchInterviewRecordings,
  InterviewRecording,
} from "src/hooks/useFetchInterviewRecordings";
import Recording from "./Recording";
import { useAuth } from "src/contexts/AuthContext";
import { TranscriptEntry } from "../DetailsTab";

// Callable function for conversation history
const getInterviewConversationHistory = httpsCallable<
  { sessionId: string },
  { data: ConversationHistory }
>(functions, "getInterviewConversationHistory");

interface ConversationHistoryMessage {
  sender: string;
  text: string;
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
}

interface ConversationSection {
  questionId: string;
  startTime: {
    _seconds: number;
    _nanoseconds: number;
  };
  messages: ConversationHistoryMessage[];
  recordingUrl?: string;
}

interface ConversationHistory {
  transcriptions: ConversationSection[];
}

interface DisplayTranscriptEntry {
  role: "user" | "assistant";
  statement: string;
  recordingUrl?: string;
}

interface TranscriptProps {
  isLoading?: boolean;
  sessionId?: string;
  currentQuestionIndex?: number;
}

const Transcript: React.FC<TranscriptProps> = ({
  isLoading = false,
  sessionId,
  currentQuestionIndex = 1,
}) => {
  const [conversationHistory, setConversationHistory] =
    useState<ConversationHistory | null>(null);
  const [conversationLoading, setConversationLoading] =
    useState<boolean>(false);
  const [conversationError, setConversationError] = useState<string | null>(
    null
  );
  const { userDoc } = useAuth();

  const isSkipMessage = (statement: string): boolean => {
    const skipPhrases = [
      "I don't know how to answer this question, let's skip this question and move to the next one.",
    ];
    return skipPhrases.includes(statement);
  };

  const { recordings, isLoading: recordingsLoading } =
    useFetchInterviewRecordings(sessionId);

  const currentQuestionRecordings = React.useMemo(() => {
    // For Question 1, combine recordings from index 0 and 1
    if (currentQuestionIndex === 1) {
      const combinedRecordings = recordings.filter(
        (recording) =>
          recording.questionIndex === 0 || recording.questionIndex === 1
      );

      return combinedRecordings.sort((a, b) => {
        const aTime = new Date(a.uploadTime || 0).getTime();
        const bTime = new Date(b.uploadTime || 0).getTime();
        return aTime - bTime;
      });
    }

    // For other questions, use the direct index
    const filteredRecordings = recordings.filter(
      (recording) => recording.questionIndex === currentQuestionIndex
    );

    return filteredRecordings.sort((a, b) => {
      const aTime = new Date(a.uploadTime || 0).getTime();
      const bTime = new Date(b.uploadTime || 0).getTime();
      return aTime - bTime;
    });
  }, [recordings, currentQuestionIndex]);

  useEffect(() => {
    async function fetchConversationHistory() {
      if (!sessionId) return;

      setConversationLoading(true);
      try {
        const result = await getInterviewConversationHistory({
          sessionId,
        });
        setConversationHistory(result.data.data);
        setConversationLoading(false);
      } catch (err) {
        setConversationError("Failed to fetch conversation history");
        setConversationLoading(false);
      }
    }

    fetchConversationHistory();
  }, [sessionId]);

  const getCurrentSectionData = () => {
    if (!conversationHistory || !conversationHistory.transcriptions) {
      return { messages: [], recordingUrl: undefined };
    }

    // For Question 1 (currentQuestionIndex === 1), combine sections 0 and 1
    if (currentQuestionIndex === 1) {
      const section0 = conversationHistory.transcriptions[0];
      const section1 = conversationHistory.transcriptions[1];

      const combinedMessages = [
        ...(section0?.messages || []),
        ...(section1?.messages || []),
      ];

      return {
        messages: combinedMessages,
        recordingUrl: section0?.recordingUrl || section1?.recordingUrl,
      };
    }

    // For other questions, adjust the index (subtract 1 to account for combined sections)
    const sectionIndex = currentQuestionIndex;
    const section = conversationHistory.transcriptions[sectionIndex];

    if (!section) {
      return { messages: [], recordingUrl: undefined };
    }

    return {
      messages: section.messages || [],
      recordingUrl: section.recordingUrl,
    };
  };

  const { messages } = getCurrentSectionData();

  // Find the recording for the current user entry
  const findRecordingForEntry = (entry: any, index: number) => {
    if (entry.role !== "user") return undefined;

    // Count how many user entries we've seen up to this index (inclusive)
    const userEntryCount = displayTranscript
      .slice(0, index + 1)
      .filter((e: DisplayTranscriptEntry) => e.role === "user").length;

    // Get the recording directly from the array using the userEntryCount as index
    const recordingIndex = userEntryCount - 1;

    // Get the recording if available
    const recording = currentQuestionRecordings[recordingIndex];

    if (recording) {
      return {
        url: recording.url,
        duration: recording.duration,
      };
    }

    return undefined;
  };

  // Use messages from conversation history or fall back to transcript prop
  const displayTranscript: DisplayTranscriptEntry[] =
    messages.length > 0
      ? messages.map(
          (msg): DisplayTranscriptEntry => ({
            role: msg.sender === "candidate" ? "user" : "assistant",
            statement: msg.text,
            recordingUrl: undefined, // We'll find the matching recording in findRecordingForEntry
          })
        )
      : [];

  if (isLoading || conversationLoading || recordingsLoading) {
    return (
      <div className="bg-bg-primary rounded-lg shadow-sm border border-border">
        <div className="bg-bg-tertiary p-4 rounded-t-lg">
          <h3 className="text-lg font-semibold text-text-primary">
            Transcript
          </h3>
        </div>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (conversationError) {
    console.error(conversationError);
    // We still proceed with showing the transcript prop data as fallback
  }

  return (
    <div className="bg-bg-primary rounded-lg shadow-sm border border-border">
      <div className="bg-bg-tertiary p-4 rounded-t-lg">
        <h3 className="text-lg font-semibold text-text-primary">Transcript</h3>
      </div>

      <div className="p-4 max-h-[500px] overflow-y-auto">
        {displayTranscript.length === 0 ? (
          <div className="text-center py-6 text-text-tertiary">
            No transcript available for this question
          </div>
        ) : (
          <div className="space-y-4">
            {displayTranscript.map(
              (entry: DisplayTranscriptEntry, index: number) => {
                const isUser = entry.role === "user";
                const recording = isUser
                  ? findRecordingForEntry(entry, index)
                  : undefined;

                return (
                  <div key={index} className="flex gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isUser ? "bg-primary-bg" : "bg-bg-tertiary"
                      }`}
                    >
                      {isUser ? (
                        <User className="w-4 h-4 text-primary" />
                      ) : (
                        <Bot className="w-4 h-4 text-text-secondary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-[14px] font-medium text-text-primary mb-1">
                        {isUser
                          ? userDoc?.survey.name || "User"
                          : "WeKruit Interviewer"}
                      </p>
                      <p className="text-[14px] text-text-secondary leading-relaxed">
                        {entry.statement}
                      </p>

                      {isUser && (
                        <div className="mt-4">
                          {isSkipMessage(entry.statement) ? (
                            <div className="flex items-center gap-2 text-[14px] text-text-tertiary">
                              <MicOff className="w-4 h-4" />
                              <span>
                                Audio not available - Question skipped
                              </span>
                            </div>
                          ) : recording?.url ? (
                            <Recording
                              url={recording.url}
                              questionId={`q${currentQuestionIndex}_${index}`}
                              initialDuration={recording.duration}
                            />
                          ) : (
                            <div className="flex items-center gap-2 text-[14px] text-text-tertiary">
                              <MicOff className="w-4 h-4" />
                              <span>No recording available</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Transcript;
